import { useState, useCallback } from 'react';
import { termsAPI } from '../api';
import { getErrorMessage } from '@/api/unwrapQuery';

/**
 * Hook for loading the active terms version and accepting it.
 * Used by the KYC consent checkbox and the after-login gate.
 *
 * @param {Object} options
 * @param {string} [options.token] - public KYC link token; if provided, acceptance is
 *   recorded through the link endpoint instead of the authenticated one.
 * @param {() => void} [options.onAccepted] - called after a successful acceptance.
 */
export const useTermsConsent = ({ token, onAccepted } = {}) => {
    const [terms, setTerms] = useState(null);
    const [loading, setLoading] = useState(false);
    const [accepting, setAccepting] = useState(false);
    const [error, setError] = useState(null);

    const loadTerms = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await termsAPI.getActive();
            if (response?.success) {
                setTerms(response.data);
                return response.data;
            }
            setError(response?.message || 'خطا در دریافت قوانین');
            return null;
        } catch (err) {
            setError(getErrorMessage(err, 'خطا در دریافت قوانین'));
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const accept = useCallback(async () => {
        setAccepting(true);
        setError(null);
        try {
            const response = token
                ? await termsAPI.linkAccept(token)
                : await termsAPI.accept(terms?.id);
            if (response?.success) {
                onAccepted && onAccepted();
                return true;
            }
            setError(response?.message || 'خطا در ثبت پذیرش قوانین');
            return false;
        } catch (err) {
            setError(getErrorMessage(err, 'خطا در ثبت پذیرش قوانین'));
            return false;
        } finally {
            setAccepting(false);
        }
    }, [token, terms, onAccepted]);

    return {
        terms,
        loading,
        accepting,
        error,
        loadTerms,
        accept,
    };
};

export default useTermsConsent;